import React, { useState, useEffect } from "react";
import { API_ENDPOINTS } from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import UnitBasicInfoSection from "./UnitFormSections/UnitBasicInfoSection";
import UnitDetailsSection from "./UnitFormSections/UnitDetailsSection";
import UnitPricingSection from "./UnitFormSections/UnitPricingSection";
import UnitImagesSection from "./UnitFormSections/UnitImagesSection";

// ── Styles ─────────────────────────────────────────────────────────────────────
const ms = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1060,
    padding: "1rem",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  modal: {
    background: "#fff",
    borderRadius: 14,
    width: "100%",
    maxWidth: 800,
    maxHeight: "92vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
    overflow: "hidden",
  },
  head: {
    padding: "1rem 1.25rem",
    borderBottom: "1px solid #e5e7eb",
    background: "#fafafa",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: 15,
    fontWeight: 700,
    color: "#1a1d23",
    flexShrink: 0,
  },
  body: { padding: "1.25rem", overflowY: "auto", flex: 1 },
  foot: {
    padding: "0.875rem 1.25rem",
    borderTop: "1px solid #e5e7eb",
    background: "#fafafa",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  btn: {
    fontSize: 12, fontWeight: 500, padding: "6px 16px", borderRadius: 6,
    border: "1px solid #d1d5db", background: "transparent", color: "#374151", cursor: "pointer",
  },
  btnOutline: {
    fontSize: 12, fontWeight: 500, padding: "6px 16px", borderRadius: 6,
    border: "1px solid #1E40AF", background: "transparent", color: "#1E40AF", cursor: "pointer",
  },
  btnPrimary: {
    fontSize: 12, fontWeight: 600, padding: "6px 18px", borderRadius: 6,
    border: "none", background: "#1E40AF", color: "#fff", cursor: "pointer",
  },
  closeBtn: {
    background: "none", border: "none", cursor: "pointer",
    fontSize: 20, lineHeight: 1, color: "#6b7280", padding: "0 2px",
  },
  errorBanner: {
    fontSize: 12, color: "#991B1B", background: "#FEF2F2",
    border: "1px solid #FECACA", borderRadius: 8, padding: "8px 12px", marginBottom: "1rem",
  },
  spinner: {
    display: "inline-block", width: 12, height: 12,
    border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff",
    borderRadius: "50%", animation: "spin 0.7s linear infinite",
    verticalAlign: "middle", marginRight: 6,
  },
};

// ── Helpers ────────────────────────────────────────────────────────────────────
const AMENITY_OPTIONS = [
  { value: "parking",            label: "Parking" },
  { value: "garage",             label: "Garage" },
  { value: "pool",               label: "Pool" },
  { value: "gym",                label: "Gym" },
  { value: "laundry",            label: "Laundry" },
  { value: "dishwasher",         label: "Dishwasher" },
  { value: "air_conditioning",   label: "Air Conditioning" },
  { value: "heating",            label: "Heating" },
  { value: "fireplace",          label: "Fireplace" },
  { value: "balcony",            label: "Balcony" },
  { value: "garden",             label: "Garden" },
  { value: "pet_friendly",       label: "Pet Friendly" },
  { value: "utilities_included", label: "Utilities Included" },
  { value: "internet",           label: "Internet" },
  { value: "doorman",            label: "Doorman" },
  { value: "hot_cold",           label: "Hot & Cold" },
  { value: "semi_furnished",     label: "Semi-furnished" },
  { value: "full_furnished",     label: "Full-furnished" },
  { value: "unfurnished",        label: "Unfurnished" },
];

const initializeFormData = (unit) => ({
  unitNumber: unit?.unitNumber || "",
  floor: unit?.floor || "",
  title: unit?.title || "",
  description: unit?.description || "",
  type: unit?.type || "one_time_rental",
  status: unit?.status || "available",
  bedrooms: unit?.propertyDetails?.bedrooms || "",
  bathrooms: unit?.propertyDetails?.bathrooms || "",
  squareFootage: unit?.propertyDetails?.squareFootage || "",
  propertyType: unit?.propertyDetails?.propertyType || "",
  layout: unit?.propertyDetails?.layout || "",
  rent: unit?.pricing?.rent || "",
  salePrice: unit?.pricing?.salePrice || "",
  deposit: unit?.pricing?.deposit || "",
  utilities: unit?.pricing?.utilities || "tenant_pays",
  amenities: unit?.amenities || [],
  images: unit?.images || [],
});

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (err) => reject(err);
  });

const buildUnitPayload = async (formData, parentPropertyId) => {
  const payload = {
    parentProperty: parentPropertyId,
    unitNumber: formData.unitNumber,
    floor: parseInt(formData.floor) || null,
    title: formData.title,
    description: formData.description,
    type: formData.type,
    status: formData.status,
    propertyDetails: {
      bedrooms: parseInt(formData.bedrooms) || 0,
      bathrooms: parseFloat(formData.bathrooms) || 0,
      squareFootage: parseInt(formData.squareFootage) || 0,
      propertyType: formData.propertyType,
      layout: formData.layout,
    },
    pricing: {
      rent: parseFloat(formData.rent) || null,
      salePrice: parseFloat(formData.salePrice) || null,
      deposit: parseFloat(formData.deposit) || null,
      utilities: formData.utilities || "tenant_pays",
    },
    amenities: formData.amenities || [],
  };
  if (formData.images && formData.images.length > 0) {
    payload.images = await Promise.all(
      formData.images.map(async (image) => {
        if (image.file && image.isNew) {
          const base64Url = await fileToBase64(image.file);
          return { url: base64Url, caption: image.caption || "", isPrimary: image.isPrimary || false };
        }
        return { url: typeof image === "string" ? image : image.url, caption: image.caption || "", isPrimary: image.isPrimary || false };
      })
    );
  }
  return payload;
};

// ── Component ──────────────────────────────────────────────────────────────────
function UnitFormModal({ show, onHide, unit, propertyId, onUnitUpdated, mode = "edit", onAddAnother }) {
  const isCreateMode = mode === "create";
  const [formData, setFormData] = useState(() => initializeFormData(unit));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    if (show) {
      setFormData(initializeFormData(isCreateMode ? null : unit));
      setError("");
    }
  }, [show, unit, isCreateMode]);

  const STATUS_DEFAULTS = {
    one_time_rental: "available",
    property_management: "active",
    sale: "available",
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "type") {
        updated.status = STATUS_DEFAULTS[value] || "available";
      }
      return updated;
    });
  };

  const handleImagesChange = (images) => setFormData((prev) => ({ ...prev, images }));

  const handleAmenitiesChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      amenities: checked
        ? [...(prev.amenities || []), value]
        : (prev.amenities || []).filter((a) => a !== value),
    }));
  };

  const handleSubmit = async (e, addAnother = false) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload = await buildUnitPayload(formData, propertyId || unit?.parentProperty);
      const endpoint = isCreateMode ? API_ENDPOINTS.units.create : API_ENDPOINTS.units.update(unit._id);
      const method = isCreateMode ? "POST" : "PUT";
      const response = await fetch(endpoint, {
        method,
        headers: { "Content-Type": "application/json", "ngrok-skip-browser-warning": "true", ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Failed to ${isCreateMode ? "create" : "update"} unit`);
      const result = await response.json();
      if (addAnother) {
        setFormData(initializeFormData(null));
        if (onAddAnother) onAddAnother(result.data);
      } else {
        onUnitUpdated(result.data, isCreateMode);
        onHide();
      }
    } catch (err) {
      setError(err.message || `An error occurred while ${isCreateMode ? "creating" : "updating"} the unit`);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div style={ms.overlay} onClick={() => { if (!loading) onHide(); }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={ms.modal} onClick={(e) => e.stopPropagation()}>
        <div style={ms.head}>
          {isCreateMode ? "Add Unit" : "Edit Unit"}
          {!loading && <button style={ms.closeBtn} onClick={onHide}>×</button>}
        </div>

        <form onSubmit={(e) => handleSubmit(e, false)} style={{ display: "contents" }}>
          <div style={ms.body}>
            {error && <div style={ms.errorBanner}>{error}</div>}
            <UnitBasicInfoSection formData={formData} handleInputChange={handleInputChange} />
            <UnitDetailsSection formData={formData} handleInputChange={handleInputChange} />

            {/* Amenities */}
            <div style={{ marginBottom: "1.25rem" }}>
              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: "0.07em",
                textTransform: "uppercase", color: "#6b7280", marginBottom: "0.6rem",
              }}>
                Amenities
              </div>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                gap: "0.4rem 1rem",
              }}>
                {AMENITY_OPTIONS.map(({ value, label }) => (
                  <label key={value} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, cursor: "pointer", color: "#374151" }}>
                    <input
                      type="checkbox"
                      value={value}
                      checked={(formData.amenities || []).includes(value)}
                      onChange={handleAmenitiesChange}
                      style={{ accentColor: "#1E40AF", width: 14, height: 14 }}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            <UnitPricingSection formData={formData} handleInputChange={handleInputChange} />
            <UnitImagesSection formData={formData} handleImagesChange={handleImagesChange} />
          </div>

          <div style={ms.foot}>
            <button type="button" style={ms.btn} onClick={onHide} disabled={loading}>
              Cancel
            </button>
            <div style={{ display: "flex", gap: 8 }}>
              {isCreateMode && onAddAnother && (
                <button
                  type="button"
                  style={ms.btnOutline}
                  onClick={(e) => handleSubmit(e, true)}
                  disabled={loading}
                >
                  Save &amp; Add Another
                </button>
              )}
              <button type="submit" style={ms.btnPrimary} disabled={loading}>
                {loading ? (
                  <><span style={ms.spinner} />{isCreateMode ? "Creating…" : "Updating…"}</>
                ) : (
                  isCreateMode ? "Create Unit" : "Update Unit"
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UnitFormModal;
